import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { BarChart3, Home, CalendarClock, Eye, ChevronRight } from "lucide-react";

const Tile = ({ icon: Icon, label, children, testid, onClick }) => (
    <button
        type="button"
        data-testid={testid}
        onClick={onClick}
        className="pd-card group flex min-h-[112px] flex-col rounded-2xl p-4 text-left outline-none focus-visible:ring-2 focus-visible:ring-[var(--pd-accent-border)]"
    >
        <div className="flex items-center gap-2">
            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-[var(--pd-icon-bg)] text-[var(--pd-icon)]"><Icon size={14} /></span>
            <span className="flex-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--pd-muted)]">{label}</span>
            <ChevronRight size={13} className="text-[var(--pd-muted)] opacity-0 transition-opacity group-hover:opacity-100" />
        </div>
        <div className="mt-3 flex-1">{children}</div>
    </button>
);

export default function ProQuickTiles({ sub, properties, onOpenSection }) {
    const [stats, setStats] = useState(null);
    useEffect(() => { api.get("/me/stats").then((r) => setStats(r.data)).catch(() => setStats({})); }, []);

    const summary = stats?.summary || {};
    const last = properties?.[0];
    const freePeriod = sub?.free_period;
    const deadline = freePeriod?.status === "grace" ? freePeriod.grace_expires_at : freePeriod?.expires_at || sub?.plan_expires_at;
    const daysLeft = freePeriod?.days_left ?? sub?.days_left;
    const deadlineLabel = freePeriod?.status === "active" ? "Fine periodo gratuito" : freePeriod?.status === "grace" ? "Fine tolleranza" : sub?.auto_renew ? "Prossimo rinnovo" : "Scadenza piano";

    return (
        <div data-testid="pro-quick-tiles" className="pro-dash mt-7 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Tile icon={BarChart3} label="Statistiche rapide" testid="pro-tile-stats" onClick={() => onOpenSection("statistiche")}>
                <div className="grid grid-cols-3 gap-1">
                    {[["Annunci", summary.total_listings], ["Visual.", summary.total_views], ["Contatti", summary.total_contacts]].map(([l, v]) => (
                        <div key={l}>
                            <div className="text-xl font-bold leading-none text-[var(--pd-text)]">{stats ? (v || 0) : "–"}</div>
                            <div className="mt-1 text-[10px] text-[var(--pd-muted)]">{l}</div>
                        </div>
                    ))}
                </div>
            </Tile>

            <Tile icon={Home} label="Ultimo annuncio" testid="pro-tile-last-listing" onClick={() => onOpenSection(last ? "annunci" : "nuovo-annuncio")}>
                {last ? (
                    <div className="flex items-center gap-3">
                        {last.photos?.[0]
                            ? <img src={last.photos[0]} alt="" className="h-12 w-16 shrink-0 rounded-lg object-cover" />
                            : <span className="grid h-12 w-16 shrink-0 place-items-center rounded-lg bg-[var(--pd-surface-2)] text-[var(--pd-icon)]"><Home size={16} /></span>}
                        <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-[var(--pd-text)]">{last.title}</div>
                            <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-[var(--pd-muted)]">{last.city} · <Eye size={11} /> {last.views || 0}</div>
                        </div>
                    </div>
                ) : (
                    <div className="text-xs text-[var(--pd-muted)]">Nessun annuncio pubblicato. Clicca per crearne uno.</div>
                )}
            </Tile>

            <Tile icon={CalendarClock} label={deadlineLabel} testid="pro-tile-deadline" onClick={() => onOpenSection("report")}>
                {deadline ? (
                    <>
                        <div className="text-xl font-bold leading-none text-[var(--pd-text)]">{new Date(deadline).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}</div>
                        <div className="mt-1 text-[11px] text-[var(--pd-muted)]">
                            {daysLeft != null && <span className="font-semibold text-[var(--pd-accent)]">{daysLeft} giorni</span>}{daysLeft != null ? " · " : ""}{sub?.plan_name}
                        </div>
                    </>
                ) : (
                    <div className="text-xs text-[var(--pd-muted)]">{sub?.plan_name || "Nessuna scadenza in programma"}</div>
                )}
            </Tile>
        </div>
    );
}
